import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Leaf, Droplet, Heart, Truck, Sparkles, Phone, Mail, MapPin, Instagram, Facebook, Youtube, Flower2, Sun, Wind, Menu, X, Star, Quote, ShoppingBag, ShoppingCart } from 'lucide-react';
import SeasonalIcons from '../components/SeasonalIcons';
import PhotoLightbox from '../components/PhotoLightbox';
import { useCart } from '../lib/CartContext';
import { getSiteContent, subscribeNewsletter, getTestimonials, getNewsletterCount, createCheckout } from '../lib/api';
import '../styles/LandingPageRevlon.css';

const FALLBACK = {
  banner: { text: 'OPENING SOON', visible: true },
  hero: { label: 'Shop', title_line1: 'Nourish Every Strand.', title_cursive: 'Love Every Crown.', description: 'Four blends. Four textures. One purpose.\nTargeted care for healthier hair,\nstronger roots, and lasting growth.', image: '/images/shop-page.png' },
  blends: [
    { name: 'ADALISSA', cursive: 'Fine & Delicate Strands', desc: 'Lightweight moisture that hydrates without weighing hair down.', hairType: 'Fine Hair', color: '#4A5D3A', image: '/images/adalissa-bottle.jpg', lookup_key: 'adalissa_one_time', price: 45 },
    { name: 'CORA', cursive: 'Defined Curls', desc: 'Enhances curl definition while leaving hair soft, hydrated, and full of shine.', hairType: 'Curly Hair', color: '#4A5D3A', image: '/images/cora-product.png', lookup_key: 'cora_one_time', price: 45 },
    { name: 'MARY', cursive: 'Protective Styles & Locs', desc: 'Helps nourish the scalp and maintain healthy-looking hair in braids, twists, locs, and wigs.', hairType: 'Protective Styles & Locks', color: '#6b2737', image: '/images/mary-product.png', lookup_key: 'mary_one_time', price: 45 },
    { name: 'EVA', cursive: 'Dense Coils & Kinks', desc: 'Rich moisture for coily textures to help soften, nourish, and support healthy-looking hair.', hairType: 'Coily & Kinky (4C)', color: '#6B7F5A', image: '/images/eva-bottle.jpg', lookup_key: 'eva_one_time', price: 45 },
  ],
  find_blend: { title: 'Find Your', title_cursive: 'Perfect Blend', description: "Every crown is unique. Take our quick quiz to discover which Cora's Naturals blend is made for your hair.", image: '/images/find-blend.png' },
  trust_badges: [
    { title: 'Clean & Pure', subtitle: 'No harsh chemicals or fillers' },
    { title: 'Plant Powered', subtitle: 'Botanical oils that nourish & restore' },
    { title: 'Made with Love', subtitle: 'Blended with intention for every crown' },
    { title: 'Fast Shipping', subtitle: 'Secure packaging. Ships with care.' },
  ],
  contact: { phone: '561 739 0940', email: 'Corasnaturals@gmail.com', location: 'Wellington, FL', social_handle: '@coras.naturals', instagram: 'https://instagram.com/coras.naturals', facebook: '', youtube: '' },
  footer: { tagline: 'Rooted in heritage. Powered by nature.', tagline_cursive: 'Made for every texture. Loved by every crown.' },
  shopify_url: 'https://czvzsu-1d.myshopify.com',
};

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" }
};

const LandingPage = () => {
  const navigate = useNavigate();
  const cart = useCart();
  const [c, setC] = useState(FALLBACK);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [testimonials, setTestimonials] = useState([]);
  const [subCount, setSubCount] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [refCode, setRefCode] = useState('');

  useEffect(() => {
    getSiteContent()
      .then(res => { if (res.data) setC({ ...FALLBACK, ...res.data }); })
      .catch(() => {});
    getTestimonials()
      .then(res => { if (res.data) setTestimonials(res.data.filter(t => t.is_approved)); })
      .catch(() => {});
    getNewsletterCount()
      .then(res => { if (res.data) setSubCount(res.data.count); })
      .catch(() => {});
    // Capture referral code from URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('ref')) setRefCode(params.get('ref'));
  }, []);

  // Merge API blends with FALLBACK to preserve lookup_key and price
  const blends = (c.blends || FALLBACK.blends).map((blend, i) => ({
    ...FALLBACK.blends[i],
    ...blend,
    lookup_key: FALLBACK.blends[i]?.lookup_key || blend.lookup_key,
    price: FALLBACK.blends[i]?.price || blend.price || 45
  }));
  const hero = c.hero || FALLBACK.hero;
  const banner = c.banner || FALLBACK.banner;
  const findBlend = c.find_blend || FALLBACK.find_blend;
  const trustBadges = c.trust_badges || FALLBACK.trust_badges;
  const contact = c.contact || FALLBACK.contact;
  const footer = c.footer || FALLBACK.footer;

  const handleContact = (e) => {
    e.preventDefault();
    window.location.href = `mailto:${contact.email}?subject=Inquiry from ${formData.name}&body=${formData.message}`;
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    try {
      const res = await subscribeNewsletter(newsletterEmail, refCode);
      setNewsletterStatus(res.data.message);
      if (res.data.referral_code) {
        const link = `${window.location.origin}?ref=${res.data.referral_code}`;
        setNewsletterStatus(`${res.data.message} Share your link: ${link}`);
      }
      setNewsletterEmail('');
      setSubCount(prev => prev + 1);
    } catch (err) {
      setNewsletterStatus(err.response?.data?.detail || 'Something went wrong');
    }
  };

  const scrollTo = (id) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const trustIcons = [<Leaf size={24} strokeWidth={1} />, <Droplet size={24} strokeWidth={1} />, <Heart size={24} strokeWidth={1} />, <Truck size={24} strokeWidth={1} />];

  return (
    <div className="landing-page" data-testid="landing-page">
      <SeasonalIcons />

      {/* Header */}
      <header className="header-nav" data-testid="header-nav">
        <div className="nav-container">
          <div className="logo-section" onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }} style={{ cursor: 'pointer' }} data-testid="logo-home-link">
            <img src="/images/coras-logo.png" alt="Cora's Naturals" className="logo-image" />
          </div>
          <nav className="nav-links">
            <button onClick={() => scrollTo('hero')} className="nav-link" data-testid="nav-home">Home</button>
            <button onClick={() => scrollTo('collection')} className="nav-link" data-testid="nav-blends">Our Blends</button>
            <button onClick={() => navigate('/founder')} className="nav-link" data-testid="nav-founder">Founder</button>
            <button onClick={() => scrollTo('find-blend')} className="nav-link" data-testid="nav-find-blend">Find Your Blend</button>
            <button onClick={() => scrollTo('contact')} className="nav-link" data-testid="nav-contact">Contact</button>
            <button className="cart-icon-btn" onClick={() => cart.setIsOpen(true)} data-testid="nav-cart">
              <ShoppingBag size={20} />
              {cart.totalItems > 0 && <span className="cart-badge">{cart.totalItems}</span>}
            </button>
          </nav>
          <button className="hamburger-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="hamburger-btn">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="mobile-menu-overlay"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25 }}
            data-testid="mobile-menu"
          >
            <button onClick={() => scrollTo('hero')} className="mobile-menu-link">Home</button>
            <button onClick={() => scrollTo('collection')} className="mobile-menu-link">Our Blends</button>
            <button onClick={() => { setMobileMenuOpen(false); navigate('/founder'); }} className="mobile-menu-link">Founder</button>
            <button onClick={() => scrollTo('find-blend')} className="mobile-menu-link">Find Your Blend</button>
            <button onClick={() => scrollTo('contact')} className="mobile-menu-link">Contact</button>
            <button onClick={() => { setMobileMenuOpen(false); navigate('/track-order'); }} className="mobile-menu-link">Track Order</button>
            <button onClick={() => { setMobileMenuOpen(false); cart.setIsOpen(true); }} className="mobile-menu-link mobile-shop-btn">Cart ({cart.totalItems})</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Opening Soon Banner with Newsletter */}
      {banner.visible !== false && (
        <div className="opening-soon-banner" data-testid="opening-soon-banner">
          <div className="banner-top-row">
            <Sparkles size={14} />
            <span>{banner.text}</span>
            <Sparkles size={14} />
          </div>
          <form className="banner-newsletter" onSubmit={handleNewsletter} data-testid="newsletter-form">
            <input
              type="email"
              placeholder="Enter your email to get notified"
              value={newsletterEmail}
              onChange={e => setNewsletterEmail(e.target.value)}
              className="newsletter-input"
              data-testid="newsletter-email-input"
            />
            <button type="submit" className="newsletter-btn" data-testid="newsletter-submit-btn">Notify Me</button>
          </form>
          {newsletterStatus && <p className="newsletter-status" data-testid="newsletter-status">{newsletterStatus}</p>}
          {subCount > 0 && !newsletterStatus && (
            <p className="newsletter-status" data-testid="subscriber-count">Join {subCount} others waiting for launch!</p>
          )}
        </div>
      )}

      {/* Hero Section */}
      <section id="hero" className="hero-section" data-testid="hero-section">
        <div className="hero-content">
          <motion.div className="hero-text" initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
            <span className="hero-label">{hero.label}</span>
            <h1 className="hero-title" data-testid="hero-title">
              {hero.title_line1}
              <span className="hero-title-cursive">{hero.title_cursive}</span>
            </h1>
            <div className="hero-divider">
              <div className="hero-divider-line" />
              <Leaf size={16} className="hero-divider-icon" />
              <div className="hero-divider-line" />
            </div>
            <p className="hero-description">
              {hero.description?.split('\n').map((line, i) => (
                <React.Fragment key={i}>{line}{i < hero.description.split('\n').length - 1 && <br />}</React.Fragment>
              ))}
            </p>
            <div className="hero-actions">
              <button className="btn-shop" onClick={() => scrollTo('collection')} data-testid="hero-shop-btn">EXPLORE BLENDS</button>
            </div>
            <div className="hero-badges">
              <div className="hero-badge">
                <div className="hero-badge-icon"><Leaf size={28} strokeWidth={1} /></div>
                <div className="hero-badge-title">Natural</div>
                <div className="hero-badge-subtitle">Botanical Ingredients</div>
              </div>
              <div className="hero-badge">
                <div className="hero-badge-icon"><Droplet size={28} strokeWidth={1} /></div>
                <div className="hero-badge-title">Nourishing</div>
                <div className="hero-badge-subtitle">Deep Hydration</div>
              </div>
              <div className="hero-badge">
                <div className="hero-badge-icon"><Sparkles size={28} strokeWidth={1} /></div>
                <div className="hero-badge-title">Results Driven</div>
                <div className="hero-badge-subtitle">Stronger Roots</div>
              </div>
            </div>
          </motion.div>
          <motion.div className="hero-image-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }}>
            <img src={hero.image} alt="Cora's Naturals Collection" className="hero-image" data-testid="hero-image" />
          </motion.div>
        </div>
      </section>

      {/* Animated Section Divider */}
      <div className="section-icon-divider">
        <motion.div animate={{ y: [-6, 6, -6], rotate: [0, 10, -10, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>
          <Leaf size={20} color="#4A5D3A" strokeWidth={1} opacity={0.4} />
        </motion.div>
        <motion.div animate={{ y: [6, -6, 6] }} transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}>
          <Flower2 size={16} color="#6B7F5A" strokeWidth={1} opacity={0.3} />
        </motion.div>
        <motion.div animate={{ y: [-4, 8, -4], rotate: [0, -15, 15, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}>
          <Sparkles size={18} color="#4A5D3A" strokeWidth={1} opacity={0.35} />
        </motion.div>
      </div>

      {/* Meet the Collection */}
      <section id="collection" className="collection-section" data-testid="collection-section">
        <div className="section-container">
          <motion.div className="section-header" {...fadeInUp}>
            <h2 className="section-title">MEET THE COLLECTION</h2>
            <div className="section-divider">
              <div className="section-divider-line" />
              <Leaf size={14} className="hero-divider-icon" />
              <div className="section-divider-line" />
            </div>
            <p className="section-subtitle">Targeted care for every texture and every crown.</p>
          </motion.div>
          <motion.div className="collection-grid" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            {blends.map((blend, i) => (
              <motion.div
                key={blend.name}
                className="collection-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                data-testid={`blend-card-${blend.name.toLowerCase()}`}
              >
                {blend.image ? (
                  <img src={blend.image} alt={blend.name} className="collection-card-img" />
                ) : (
                  <div className="collection-card-label" style={{ background: blend.color }}>
                    <h4>{blend.name}</h4>
                  </div>
                )}
                <div className="collection-card-info">
                  <h3 className="collection-card-name">{blend.name}</h3>
                  <p className="collection-card-cursive">{blend.cursive}</p>
                  <p className="collection-card-desc">{blend.desc}</p>
                  <p className="collection-card-price">${blend.price || 45}</p>
                  <div className="collection-card-actions">
                    <button
                      className="btn-add-cart"
                      onClick={() => cart.addItem({ name: blend.name, lookup_key: blend.lookup_key, price: blend.price || 45, image: blend.image })}
                      data-testid={`add-cart-${blend.name.toLowerCase()}`}
                    >
                      <ShoppingCart size={14} /> Add to Cart
                    </button>
                    <button
                      className="btn-buy-now"
                      onClick={async () => {
                        try {
                          const res = await createCheckout(blend.lookup_key, 1, window.location.origin);
                          if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
                        } catch { alert('Checkout failed'); }
                      }}
                      data-testid={`buy-now-${blend.name.toLowerCase()}`}
                    >
                      Buy Now
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Animated Divider */}
      <div className="section-icon-divider">
        <motion.div animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}>
          <Wind size={18} color="#4A5D3A" strokeWidth={1} opacity={0.3} />
        </motion.div>
        <motion.div animate={{ y: [-5, 5, -5], x: [-3, 3, -3] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}>
          <Leaf size={22} color="#6B7F5A" strokeWidth={1} opacity={0.4} />
        </motion.div>
        <motion.div animate={{ scale: [0.9, 1.1, 0.9] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}>
          <Sun size={16} color="#D4AF37" strokeWidth={1} opacity={0.3} />
        </motion.div>
      </div>

      {/* Find Your Blend */}
      <section id="find-blend" className="blend-section" data-testid="find-blend-section">
        <div className="blend-content">
          <motion.div className="blend-image-side" initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <img src={findBlend.image} alt="Find Your Perfect Blend" />
          </motion.div>
          <motion.div className="blend-text-side" initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <h2 className="blend-title" data-testid="blend-title">
              {findBlend.title}
              <span className="blend-title-cursive">{findBlend.title_cursive}</span>
            </h2>
            <div className="section-divider">
              <div className="section-divider-line" />
              <Leaf size={14} className="hero-divider-icon" />
              <div className="section-divider-line" />
            </div>
            <p className="blend-description">{findBlend.description}</p>
            <div className="hero-actions" style={{ justifyContent: 'center' }}>
              <button onClick={() => navigate('/find-your-blend')} className="btn-quiz" data-testid="start-quiz-btn">
                START THE QUIZ
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Animated Divider */}
      <div className="section-icon-divider">
        <motion.div animate={{ y: [-8, 8, -8], rotate: [-20, 20, -20] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>
          <Droplet size={18} color="#4A5D3A" strokeWidth={1} opacity={0.35} />
        </motion.div>
        <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}>
          <Heart size={16} color="#6b2737" strokeWidth={1} opacity={0.3} />
        </motion.div>
        <motion.div animate={{ y: [5, -5, 5], x: [3, -3, 3] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}>
          <Leaf size={20} color="#4A5D3A" strokeWidth={1} opacity={0.4} />
        </motion.div>
      </div>

      {/* 4 Blends Icons */}
      <section className="blends-icons-section" data-testid="blends-icons-section">
        <motion.div {...fadeInUp}>
          <h2 className="section-title-large">4 Blends. 4 Textures. 1 Purpose.</h2>
          <p className="section-subtitle">Nourishing each texture with nature&apos;s best.</p>
        </motion.div>
        <div className="blends-icons-grid">
          {blends.map((b, i) => (
            <motion.div key={b.name} className="blend-icon-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }} data-testid={`blend-icon-${b.name.toLowerCase()}`}>
              <div className="blend-icon-circle" style={b.image ? { overflow: 'hidden', padding: 0 } : {}}>
                {b.image ? (
                  <img src={b.image} alt={b.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                ) : (
                  <Leaf size={40} strokeWidth={1} color="#4A5D3A" />
                )}
              </div>
              <div className={`blend-icon-name ${b.name === 'CORA' ? 'cora-color' : b.name === 'MARY' ? 'mary-color' : b.name === 'EVA' ? 'eva-color' : ''}`}>{b.name}</div>
              <div className="blend-icon-type">{b.hairType}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust Badges */}
      <section className="trust-section" data-testid="trust-section">
        <div className="trust-grid">
          {trustBadges.slice(0, 4).map((badge, i) => (
            <div key={i} className="trust-item">
              <div className="trust-icon">{[<Leaf size={28} strokeWidth={1} />, <Droplet size={28} strokeWidth={1} />, <Sparkles size={28} strokeWidth={1} />, <Heart size={28} strokeWidth={1} />][i] || <Leaf size={28} strokeWidth={1} />}</div>
              <div className="trust-title">{badge.title}</div>
              <div className="trust-subtitle">{badge.subtitle}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Customer Stories / Review Gallery */}
      {testimonials.length > 0 && (
        <section className="reviews-section" data-testid="reviews-section">
          <div className="section-container">
            <motion.div className="section-header" {...fadeInUp}>
              <h2 className="section-title">REAL RESULTS</h2>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 400, marginBottom: '0.25rem' }}>
                Stories From
              </h2>
              <h2 style={{ fontFamily: "'Dancing Script', cursive", fontSize: 'clamp(2rem, 3.5vw, 3rem)', fontWeight: 400, color: '#4A5D3A' }}>
                Our Community
              </h2>
              <div className="section-divider" style={{ marginTop: '1rem' }}>
                <div className="section-divider-line" />
                <Leaf size={14} className="hero-divider-icon" />
                <div className="section-divider-line" />
              </div>
            </motion.div>
            <div className="reviews-grid">
              {testimonials.slice(0, 6).map((t, i) => (
                <motion.div
                  key={t.id}
                  className="review-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  data-testid={`review-card-${i}`}
                >
                  {(t.before_photo || t.after_photo) && (
                    <div className="review-photos" style={{ cursor: 'pointer' }} onClick={() => setLightbox(t)} data-testid={`review-photos-${i}`}>
                      {t.before_photo && (
                        <div className="review-photo-wrapper">
                          <img src={t.before_photo} alt="Before" className="review-photo" />
                          <span className="review-photo-label">Before</span>
                        </div>
                      )}
                      {t.after_photo && (
                        <div className="review-photo-wrapper">
                          <img src={t.after_photo} alt="After" className="review-photo" />
                          <span className="review-photo-label">After</span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="review-stars">
                    {[...Array(t.rating || 5)].map((_, j) => (
                      <Star key={j} size={14} fill="#4A5D3A" color="#4A5D3A" />
                    ))}
                  </div>
                  <Quote size={18} className="review-quote-icon" />
                  <p className="review-text">{t.testimonial_text}</p>
                  <div className="review-author">
                    <span className="review-name">{t.customer_name}</span>
                    {t.hair_type && <span className="review-hair-type">{t.hair_type}</span>}
                  </div>
                  {t.results_summary && <p className="review-results">{t.results_summary}</p>}
                </motion.div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
              <button onClick={() => navigate('/share-your-story')} className="btn-quiz" data-testid="share-story-btn">
                SHARE YOUR STORY
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section id="contact" className="contact-section" data-testid="contact-section">
        <div className="section-container">
          <motion.div className="section-header" {...fadeInUp}>
            <span className="hero-label">Contact</span>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 3.5vw, 3rem)', fontWeight: 400, marginBottom: '0.25rem' }}>
              We&apos;d Love to
            </h2>
            <h2 style={{ fontFamily: "'Dancing Script', cursive", fontSize: 'clamp(2.5rem, 4vw, 3.5rem)', fontWeight: 400, color: '#4A5D3A' }}>
              Hear From You.
            </h2>
            <div className="section-divider" style={{ marginTop: '1.5rem' }}>
              <div className="section-divider-line" />
              <Leaf size={14} className="hero-divider-icon" />
              <div className="section-divider-line" />
            </div>
            <p className="section-subtitle" style={{ marginTop: '1rem' }}>
              Questions about your blend, your order, or choosing the right oil?<br />We&apos;re here to help.
            </p>
          </motion.div>

          <motion.div className="contact-grid" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="contact-card" data-testid="contact-phone">
              <div className="contact-card-icon"><Phone size={24} strokeWidth={1} /></div>
              <div className="contact-card-title">Call or Text</div>
              <div className="contact-card-value">{contact.phone}</div>
            </div>
            <div className="contact-card" data-testid="contact-email">
              <div className="contact-card-icon"><Mail size={24} strokeWidth={1} /></div>
              <div className="contact-card-title">Email</div>
              <div className="contact-card-value">{contact.email}</div>
            </div>
            <div className="contact-card" data-testid="contact-location">
              <div className="contact-card-icon"><MapPin size={24} strokeWidth={1} /></div>
              <div className="contact-card-title">Location</div>
              <div className="contact-card-value">{contact.location}</div>
            </div>
            <div className="contact-card" data-testid="contact-social">
              <div className="contact-card-icon"><Heart size={24} strokeWidth={1} /></div>
              <div className="contact-card-title">Follow Us</div>
              <div className="contact-card-value">{contact.social_handle}</div>
              <div className="contact-card-social">
                {contact.instagram && <a href={contact.instagram} target="_blank" rel="noopener noreferrer"><Instagram size={20} /></a>}
                {contact.facebook && <a href={contact.facebook} target="_blank" rel="noopener noreferrer"><Facebook size={20} /></a>}
                {contact.youtube && <a href={contact.youtube} target="_blank" rel="noopener noreferrer"><Youtube size={20} /></a>}
              </div>
            </div>
          </motion.div>

          <motion.form className="contact-form" onSubmit={handleContact} {...fadeInUp} data-testid="contact-form">
            <div className="contact-form-grid">
              <input className="contact-input" placeholder="Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required data-testid="contact-name-input" />
              <input className="contact-input" type="email" placeholder="Email Address" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required data-testid="contact-email-input" />
            </div>
            <input className="contact-input" style={{ width: '100%', marginBottom: '1rem' }} placeholder="Phone Number" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} data-testid="contact-phone-input" />
            <textarea className="contact-textarea" placeholder="How can we help?" value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} required data-testid="contact-message-input" />
            <button type="submit" className="btn-send" data-testid="contact-submit">Send Message</button>
          </motion.form>
        </div>
      </section>

      {/* Trust Bottom Bar */}
      <section className="trust-section" style={{ background: '#4A5D3A' }}>
        <div className="trust-grid">
          {trustBadges.slice(0, 4).map((badge, i) => (
            <div key={i} className="trust-item">
              <div className="trust-icon" style={{ color: '#F5F0E8' }}>{trustIcons[i] || <Leaf size={24} strokeWidth={1} />}</div>
              <div className="trust-title" style={{ color: '#F5F0E8' }}>{badge.title}</div>
              <div className="trust-subtitle" style={{ color: 'rgba(245,240,232,0.7)' }}>{badge.subtitle}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img src="/images/coras-logo.png" alt="Cora's Naturals" className="footer-logo-image" />
          </div>
          <div className="footer-tagline-group">
            <p className="footer-tagline">{footer.tagline}</p>
            <p className="footer-tagline-cursive">{footer.tagline_cursive}</p>
          </div>
          <div className="footer-social">
            <div className="footer-social-title">Follow Us</div>
            <div className="footer-social-links">
              {contact.instagram && <a href={contact.instagram} target="_blank" rel="noopener noreferrer"><Instagram size={20} /></a>}
              {contact.facebook && <a href={contact.facebook} target="_blank" rel="noopener noreferrer"><Facebook size={20} /></a>}
              {contact.youtube && <a href={contact.youtube} target="_blank" rel="noopener noreferrer"><Youtube size={20} /></a>}
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 Cora&apos;s Naturals. All rights reserved. | <a href="/track-order" style={{ color: '#4A5D3A' }}>Track Order</a> | <a href="/share-your-story" style={{ color: '#4A5D3A' }}>Share Your Story</a></p>
        </div>
      </footer>

      {/* Photo Lightbox */}
      <PhotoLightbox
        isOpen={!!lightbox}
        onClose={() => setLightbox(null)}
        beforePhoto={lightbox?.before_photo}
        afterPhoto={lightbox?.after_photo}
        customerName={lightbox?.customer_name}
      />
    </div>
  );
};

export default LandingPage;
